import React, { useContext, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactApexChart from "react-apexcharts";
import ChartModal from "./ChartModal";
import { fetchTaskStatusCount } from "../../../../service/UserDashboard";
import AppContext from "../../../../context/AppContext";
import translations from "../../../common/Translations";
import { toast } from "../../../../utils/toast";
import Cookies from "js-cookie";
import Modal from "../../../DownloadModal/Modal";
import { fetchFilterExcel } from "../../../../service/Task";
import CommonButton from "../../../common/CommonButton";
import datePickerPopper from "../../../common/DatePickerPopper";
import ModalCloseButton from "../../../common/ModalCloseButton";
import ActionIconButton from "../../../common/ActionIconButton";
import {
  BarChart3,
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Eye,
  ListChecks,
} from "lucide-react";

const ITEMS_PER_PAGE = 3;
const parseYmdHm = (s) => {
  if (!s) return null;
  const [datePart, timePart = "00:00"] = String(s).split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  return new Date(y, m - 1, d, hh, mm, 0, 0);
};
const formatYmdHm = (date) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const TaskStatusChart = ({ selectedWorkflowId, selectedOrgId }) => {
  const { langMode, theme, projectId, activityId } = useContext(AppContext);
  const isRtl = langMode === "ar";
  const [taskStatus, setTaskStatus] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [chartView, setChartView] = useState("area"); // Changed default to radialBar
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState("");
  const [downloadModal, setDownloadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const [priority, setPriority] = useState("Low_Priority"); // 
  const [status, setStatus] = useState('')
  const today = new Date();

  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const startOfDayOneMonthAgo = new Date(oneMonthAgo.getFullYear(), oneMonthAgo.getMonth(), oneMonthAgo.getDate(), 0, 0, 0); // Start of day one month ago
  const [startDate, setStartDate] = useState(startOfDayOneMonthAgo.toISOString().substring(0, 16)); // YYYY-MM-DDTHH:MM
  const [endDate, setEndDate] = useState(today.toISOString().substring(0, 16));
  const [showDateModal, setShowDateModal] = useState(false);
  const todayPicker = useMemo(() => new Date(), []);
  const defaultColors = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#F472B6", "#14B8A6"]; // Indigo, Emerald, Amber, Red, Violet, Cyan, Pink, Teal
  const darkThemeColors = ["#60A5FA", "#34D399", "#FBBF24", "#F87171", "#A78BFA", "#22D3EE", "#FB7185", "#2DD4BF"]; // Lighter shades for dark theme
  const highContrastColors = ["#FFFF00", "#00FFFF", "#FF00FF", "#00FF00", "#FFD700", "#C0C0C0", "#FFC0CB", "#FFFFFF"]; // Bold for high contrast

  const getChartColors = () => {
    if (theme === "dark") return darkThemeColors;
    if (theme === "high-contrast") return highContrastColors;
    return defaultColors;
  };

  const formatLabel = (label) => {
    switch (label) {
      case "activeCount":
        return translations[langMode].active || "Active";
      case "assignedCount":
        return translations[langMode].assigned || "Assigned";
      case "completedCount":
        return translations[langMode].completed || "Completed";
      case "duplicateCount":
        return "Duplicate";
      case "finishedCount":
        return "Finished";
      case "inProgressCount":
        return translations[langMode].progress || "In Progress";
      case "onHoldCount":
        return translations[langMode].onhold || "On Hold";
      case "pendingCount":
        return "Pending";
      case "onHold":
        return translations[langMode].onhold;
      case "inProgress":
        return translations[langMode].progress;
      case "active":
        return translations[langMode].active;
      case "assigned":
        return translations[langMode].assigned;
      case "completed":
        return translations[langMode].completed;
      default:
        return label;
    }
  };

  const countValue = (...values) => {
    const value = values.find((item) => item !== undefined && item !== null);

    if (Array.isArray(value)) return value.length;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (value && typeof value === "object") {
      const nestedCount = value.count ?? value.total ?? value.totalCount ?? value.size;
      const parsed = Number(nestedCount);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  };

  const normalizeTaskStatusCounts = (value = {}) => ({
    activeCount: countValue(value.activeCount, value.active, value.activeTasks),
    assignedCount: countValue(value.assignedCount, value.assigned, value.assignedTasks),
    completedCount: countValue(value.completedCount, value.completed, value.completedTasks),
    duplicateCount: countValue(value.duplicateCount, value.duplicate, value.duplicateTasks),
    finishedCount: countValue(value.finishedCount, value.finished, value.finishedTasks),
    inProgressCount: countValue(value.inProgressCount, value.inProgress, value.inProgressTasks),
    onHoldCount: countValue(value.onHoldCount, value.onHold, value.onHoldTasks),
    pendingCount: countValue(value.pendingCount, value.pending, value.pendingTasks),
  });

  const fetchData = async () => {
    try {
      const formattedStartDate = `${startDate}:00`;
      const formattedEndDate = `${endDate}:00`;
      const data = await fetchTaskStatusCount(selectedWorkflowId, formattedStartDate, formattedEndDate);
      if (data && Object.keys(data).length > 0) {
        setTaskStatus(normalizeTaskStatusCounts(data));
        setError(""); // Clear any previous errors
      } else {
        setError(translations[langMode].noDataAvailableForWorkflow || "No task status data available for this workflow.");
        setTaskStatus({});
      }
      setCurrentPage(0); // Reset page when data changes or workflow changes
    } catch (err) {
      setError(translations[langMode].failedToFetchTaskStatus || "Failed to fetch task status. Please try again.");
      setTaskStatus({});
      setCurrentPage(0);
      console.error(err);
    }
  };
  useEffect(() => {

    if (selectedWorkflowId) {
      fetchData();
    } else {
      setError(translations[langMode].selectWorkflowPrompt || "Please select a workflow.");
      setTaskStatus({});
      setCurrentPage(0);
    }
  }, [selectedWorkflowId, langMode, translations, startDate, endDate]);

  const desiredOrder = [
    "activeCount",
    "assignedCount",
    "completedCount",
    "duplicateCount",
    "finishedCount",
    "inProgressCount",
    "onHoldCount",
    "pendingCount",
  ];
  const statusItems = desiredOrder.map((key) => ({
    key,
    label: formatLabel(key),
    value: Number(taskStatus[key]) || 0,
  }));

  const totalPages = Math.ceil(statusItems.length / ITEMS_PER_PAGE);

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    } else if (totalPages === 0 && currentPage !== 0) {
      setCurrentPage(0); // Reset if no pages
    }
  }, [totalPages, currentPage]);


  const startIndex = currentPage * ITEMS_PER_PAGE;
  const paginatedItems = statusItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const paginatedCategories = paginatedItems.map((item) => item.label);
  const paginatedData = paginatedItems.map((item) => item.value);

  const totalTasks = Object.values(taskStatus).reduce((sum, count) => sum + (Number(count) || 0), 0);
  const normalizedPaginatedData = paginatedData.map(count =>
    totalTasks > 0 ? (count / totalTasks) * 100 : 0
  );

  const baseChartOptions = {
    chart: {
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      foreColor: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333", // Global text color
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        colors: [theme === "high-contrast" ? "#000" : (theme === "dark" ? "#FFF" : "#333")], // Data label color
      },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "13px",
      labels: {
        colors: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333", // Legend label color
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    tooltip: {
      enabled: true,
      theme: theme === "dark" ? "dark" : "light",
      style: {
        fontSize: "13px",
        fontFamily: "inherit",
        background: theme === "dark" ? "#333" : theme === "high-contrast" ? "#FFF" : "#f4f4f4",
        color: theme === "dark" ? "#fff" : theme === "high-contrast" ? "#000" : "#333",
      },
      y: {
        formatter: function (val) {
          return val + " tasks"; // More descriptive tooltip
        }
      }
    },
    colors: getChartColors(), // Dynamic colors
  };

  const RadialBarChartView = ({ series, labels }) => {
    const radialBarOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "radialBar",
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '30%', // Adjust size to create concentric effect
            background: 'transparent',
            image: undefined,
          },
          track: {
            show: true,
            startAngle: undefined,
            endAngle: undefined,
            background: theme === "dark" ? "#374151" : "#e0e0e0", // Track color for un-filled part
            strokeWidth: '97%', // Make track thinner to create gap
            opacity: 1,
            margin: 10, // Margin between each ring
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              blur: 4,
              opacity: 0.15
            }
          },
          dataLabels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontFamily: 'MediumFont, Manrope, sans-serif',
              fontWeight: 600,
              color: baseChartOptions.chart.foreColor,
              offsetY: -10
            },
            value: {
              show: true,
              fontSize: '18px',
              fontFamily: 'MediumFont, Manrope, sans-serif',
              fontWeight: 700,
              color: baseChartOptions.chart.foreColor,
              offsetY: 5,
              formatter: function (val, { seriesIndex, dataPointIndex, w }) {
                const originalValue = paginatedData[seriesIndex];
                return (translations[langMode].tasksCountLabel || "{n} tasks").replace("{n}", originalValue);
              }
            },
            total: {
              show: true,
              label: translations[langMode].totalTasks || "Total Tasks",
              color: baseChartOptions.chart.foreColor,
              formatter: function (w) {
                return Object.values(taskStatus).reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: getChartColors(), // Ensure gradients use the defined colors
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100]
        }
      },
      labels: labels, // Labels for each series (outer ring)
      stroke: {
        lineCap: 'round' // Rounded ends for the bars
      },
      legend: {
        show: true, // Keep legend for clarity
        position: 'bottom',
        offsetY: 0,
        height: 50,
        markers: {
          fillColors: getChartColors()
        }
      },
    };

    return (
      <div id="task-radial-bar-chart" className="w-full h-full  ">
        <ReactApexChart
          options={radialBarOptions}
          series={series} // Use normalized data (percentages)
          type="radialBar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const DonutChartView = ({ series, labels }) => {
    const donutOptions = {
      ...baseChartOptions,
      labels: labels,
      dataLabels: {
        ...baseChartOptions.dataLabels,
        formatter: function (val, opts) {
          const total = opts.w.globals.seriesTotals.reduce((a, b) => a + b, 0);
          return total > 0 ? `${val.toFixed(1)}%` : '';
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "14px",
                color: baseChartOptions.chart.foreColor,
              },
              value: {
                show: true,
                fontSize: "18px",
                fontWeight: 600,
                color: baseChartOptions.chart.foreColor,
                formatter: function (val) {
                  return val;
                },
              },
              total: {
                show: true,
                showAlways: true,
                label: translations[langMode].totalTasks || "Total Tasks",
                fontSize: '14px',
                fontWeight: 600,
                color: baseChartOptions.chart.foreColor,
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b;
                  }, 0);
                }
              },
            },
          },
        },
      },
      stroke: {
        show: true,
        colors: [theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff")],
        width: 2
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " tasks";
          }
        }
      }
    };

    return (
      <div id="task-donut-chart" className="w-full h-full  ">
        <ReactApexChart
          options={donutOptions}
          series={series}
          type="donut"
          width="100%"
          height="100%" // Set height to 100%
        />
      </div>
    );
  };

  const BarChartView = ({ series, categories }) => {

    const safeSeries = (series || []).map((v) => Number(v) || 0);
    const safeCategories = categories?.slice(0, safeSeries.length) || [];
    const maxValue = Math.max(...safeSeries, 0);

    const barOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "bar",
        background: theme === "dark" ? "#1F2937" : "#FFFFFF",
      },
      xaxis: {
        categories: safeCategories,
        labels: {
          rotate: 0,
          trim: true,
          hideOverlappingLabels: false,
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '12px',
            fontWeight: 500,
          },
        },
        axisBorder: {
          show: true,
          color: theme === "dark" ? "#4A5568" : "#E2E8F0",
        },
        axisTicks: {
          show: true,
          color: theme === "dark" ? "#4A5568" : "#E2E8F0",
        },
      },
      yaxis: {
        min: 0,
        max: maxValue === 0 ? 1 : undefined,
        tickAmount: maxValue === 0 ? 1 : undefined,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '12px',
            fontWeight: 500,
          },
          formatter: function (val) {
            return Number.isInteger(val) ? val : "";
          }
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        title: {
          text: translations[langMode].numberOfTasks || "Number of Tasks",
          style: {
            color: baseChartOptions.chart.foreColor,
            fontSize: '14px',
            fontWeight: 600,
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 7,
          columnWidth: '42%',
          distributed: true,
          dataLabels: {
            position: 'top',
            offsetY: -18,
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -18,
        style: {
          fontSize: '13px',
          colors: [theme === "dark" ? "#FFF" : "#333"], // Ensure good contrast
          fontWeight: 600,
        },
        background: {
          enabled: true,
          foreColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
          padding: 6,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.2,
          }
        },
        formatter: function (val) {
          return val; // Display the raw count
        }
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4, // Dashed grid lines
        position: 'back',
        xaxis: {
          lines: {
            show: false // No vertical grid lines for cleaner look
          }
        },
        yaxis: {
          lines: {
            show: true // Keep horizontal grid lines for readability
          }
        },
        padding: {
          left: 0,
          right: 12,
          bottom: 4,
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val) {
            return val + " tasks";
          }
        },
        x: {
          formatter: function (val) {
            return val;
          }
        },
      },
      colors: getChartColors(), // Apply dynamic colors
      legend: {
        show: false // Hide legend for single series bar chart
      },
      responsive: [{
        breakpoint: 768, // Adjust for medium screens and smaller
        options: {
          plotOptions: {
            bar: {
              columnWidth: '48%',
            }
          },
          dataLabels: {
            offsetY: -15,
            style: {
              fontSize: '11px',
            }
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '11px',
              }
            },
            title: {
              style: {
                fontSize: '12px',
              }
            }
          }
        }
      },
      {
        breakpoint: 480, // Adjust for small screens
        options: {
          plotOptions: {
            bar: {
              columnWidth: '70%',
            }
          },
          dataLabels: {
            offsetY: -10,
            style: {
              fontSize: '10px',
            }
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            },
            title: {
              style: {
                fontSize: '11px',
              }
            }
          }
        }
      }
      ]
    };

    return (
      <div id="user-bar-chart" className="w-full h-full"> {/* Added padding */}
        <ReactApexChart
          options={barOptions}
          series={[{ name: translations[langMode].taskstatus || "Task Status", data: safeSeries || [] }]}
          type="bar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const AreaChartView = ({ series, categories }) => {
    const areaOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "area",
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100]
        }
      },
      dataLabels: {
        enabled: true,
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back'
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              parentHeightOffset: 0,
            },
            dataLabels: {
              enabled: false,
            },
            legend: {
              position: "top",
              horizontalAlign: "center",
              fontSize: "11px",
              height: 54,
              itemMargin: {
                horizontal: 5,
                vertical: 3,
              },
              markers: {
                width: 8,
                height: 8,
                radius: 8,
              },
            },
            xaxis: {
              tickAmount: 3,
              labels: {
                rotate: 0,
                rotateAlways: false,
                hideOverlappingLabels: true,
                trim: true,
                style: {
                  fontSize: "9px",
                },
                formatter: (value) => {
                  const text = String(value ?? "");
                  return text.length > 10 ? `${text.slice(0, 9)}…` : text;
                },
              },
            },
            yaxis: {
              tickAmount: 4,
              decimalsInFloat: 0,
              labels: {
                minWidth: 24,
                maxWidth: 30,
                style: {
                  fontSize: "9px",
                },
              },
            },
            grid: {
              padding: {
                left: 4,
                right: 8,
                top: 0,
                bottom: 8,
              },
            },
          },
        },
      ],
    };

    return (
      <div id="task-area-chart" className="w-full h-full  ">
        <ReactApexChart
          options={areaOptions}
          series={[{ name: translations[langMode].taskCount || "Task Count", data: series }]}
          type="area"
          width="100%"
          height="100%" // Set height to 100%
        />
      </div>
    );
  };

  const RadarChartView = ({ series, categories }) => {
    const radarOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "radar",
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      yaxis: {
        show: false,
      },
      stroke: {
        show: true,
        width: 2,
        curve: 'smooth',
        lineCap: 'round',
      },
      fill: {
        opacity: 0.4,
      },
      markers: {
        size: 5,
        hover: {
          size: 7,
        },
        colors: getChartColors(),
        strokeColors: theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff"),
        strokeWidth: 2,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          colors: [baseChartOptions.chart.foreColor]
        },
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back',
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          left: 20,
          right: 20
        }
      }
    };

    return (
      <div id="task-radar-chart" className="w-full h-full  ">
        <ReactApexChart
          options={radarOptions}
          series={[{ name: translations[langMode].taskCount || "Task Count", data: series }]}
          type="radar"
          width="100%"
          height="100%" // Set height to 100%
        />
      </div>
    );
  };

  const isDataEmpty = statusItems.every((item) => item.value === 0);

  const handleShowModal = () => {
  
    if (!selectedWorkflowId) {
      toast.warn(translations[langMode].selectWorkflowFirst);
      return;
    }
    setDownloadModal(true);
  
  };

  const downloadFiltration = async (selectedOption) => {
    setIsLoading(true);
    try {
      const userTypeCookie = Cookies.get("userType");
      const params = {
        workflowIds: activityId || "",
        priority: priority,
        active: status,
        areaCode: "",
        accountNo: "",
        organizationId: "",
        projectId: projectId || ""
      };

      const orgId = JSON.parse(localStorage.getItem("selectedOrgId"))
      if (selectedOption === "all") {
        params.organizationId = orgId || "";
        params.workflowIds = "";

      } else if (
        selectedOption === "particular" ||
        userTypeCookie !== "super_admin"
      ) {
        params.organizationId = selectedOrgId || "";

        params.workflowIds = activityId || "";

      }
      const data = await fetchFilterExcel({
        workflowIds: params.workflowIds,
        priority: params.priority,
        active: params.active,
        areaCode: params.areaCode,
        accountNo: params.accountNo,
        organizationId: params.organizationId,
        projectId: params.projectId,
        includeProjectId: selectedOption === "all"
      });
      const code = data.header?.code;
      if (code === 600) {
        const s3Link = data.body?.value;
        const link = document.createElement("a");
        link.href = s3Link;
        link.setAttribute("download", "report.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(translations[langMode].excelisdownloading);
      } else {
        toast.error(data?.body?.value || translations[langMode].norecordfound);
      }
    } catch (error) {
      console.error("Error during report download:", error);
      toast.error(translations[langMode].anerroroccurred);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidNumericArray = (arr) =>
  Array.isArray(arr) &&
  arr.length > 0 &&
  arr.every(v => Number.isFinite(Number(v)));

  const isChartDataInvalid =
  !isValidNumericArray(paginatedData) ||
  !Array.isArray(paginatedCategories) ||
  paginatedCategories.length !== paginatedData.length;


  return (
    <div className={`h-full w-full flex flex-col ${theme === "dark" ? "text-gray-200" : "text-gray-900"}`}>
      <div className="flex items-center justify-between px-1 sm:px-2 py-1 sm:py-2 w-full gap-2">
        <h2 className="flex items-center gap-1.5 sm:gap-2 text-left text-xs sm:text-base font-semibold min-w-0 flex-1">
          <ListChecks className="text-purple-500 shrink-0" size={18} />
          <label className="mt-0.5 truncate">{translations[langMode].taskstatus}</label>
        </h2>
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <ActionIconButton
            onClick={() => setShowDateModal(true)}
            tone="gray"
            className="text-gray-600 hover:text-gray-800"
            ariaLabel={translations[langMode].RangeFilter}
            tooltipText={translations[langMode].RangeFilter}
            tooltipPosition="bottom"
          >
            <CalendarDays size={18} />
          </ActionIconButton>
          <ActionIconButton
            type="button"
            onClick={handleShowModal}
            tone="gray"
            className="text-gray-500 hover:text-gray-700"
            aria-label={translations[langMode].download || "Download"}
            tooltipText={translations[langMode].download || "Download"}
            tooltipPosition="bottom"
          >
            <Download size={18} />
          </ActionIconButton>
          <ActionIconButton
            type="button"
            tone="gray"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            onClick={() => setShowModal(true)}
            aria-label={translations[langMode].ViewChart}
            tooltipText={translations[langMode].ViewChart}
            tooltipPosition="bottom"
          >
            <Eye size={18} />
          </ActionIconButton>
        </div>

      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {(error || isDataEmpty || isChartDataInvalid) ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-4">
            <span
              className={`flex h-12 w-12 items-center justify-center rounded-full
                ${theme === "dark"
                  ? "bg-[#236DB4]/20 text-[#236DB4]"
                  : theme === "high-contrast"
                    ? "bg-black text-yellow-300"
                    : "bg-[#236DB4]/10 text-[#236DB4]"
                }`}
            >
              <BarChart3 className="h-6 w-6" />
            </span>
            <div className="flex flex-col gap-1">
              <p className={`text-sm font-medium ${theme === "high-contrast" ? "text-yellow-300" : theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
                {translations[langMode].noDataAvailable || translations[langMode].notchartYet}
              </p>
              <p className={`text-sm ${theme === "high-contrast" ? "text-yellow-200" : "text-gray-500"}`}>
                {translations[langMode].chartWaitingForResponses}
              </p>
            </div>
          </div>
        ) : (
        <div className="flex-1 min-h-0 w-full flex flex-col justify-between">
          <div className="flex-1 w-full h-full"> {/* This div will take the available space for the chart */}
            {chartView === "chart" ? (
              <RadialBarChartView series={normalizedPaginatedData} labels={paginatedCategories} />
            ) : chartView === "donut" ? (
              <DonutChartView series={paginatedData} labels={paginatedCategories} />
            ) : chartView === "area" ? (
              <AreaChartView series={paginatedData} categories={paginatedCategories} />
            ) : chartView === "radar" ? (
              <RadarChartView series={paginatedData} categories={paginatedCategories} />
            ) : ( // Default to bar chart
              <BarChartView series={paginatedData} categories={paginatedCategories} />
            )}
          </div>
        </div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-shrink-0 items-center justify-between gap-2">
            <ActionIconButton
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
              tone="blue"
              className={`${currentPage === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-500 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'
                }`}
              aria-label="Previous page"
            >
              {isRtl ? (
                <ChevronsRight size={18} />
              ) : (
                <ChevronsLeft size={18} />
              )}
            </ActionIconButton>
            <span className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
              {translations[langMode].page || "Page"} {currentPage + 1} {translations[langMode].of || "of"} {totalPages}
            </span>
            <ActionIconButton
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
              }
              disabled={currentPage === totalPages - 1}
              tone="blue"
              className={`${currentPage === totalPages - 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-500 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'
                }`}
              aria-label="Next page"
            >
              {isRtl ? (
                <ChevronsLeft size={18} />
              ) : (
                <ChevronsRight size={18} />
              )}
            </ActionIconButton>
          </div>
        )}
      </div>
      {showDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div data-tooltip-bounds className={`relative max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800`}>
            <div className="mb-4 pr-8">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{translations[langMode].SelectDateTime}</h3>
            </div>
            <ModalCloseButton
              className="absolute right-4 top-4"
              onClick={() => setShowDateModal(false)}
            />
            <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="min-w-0">
              <label className="text-sm font-medium">{translations[langMode].startdatetime}</label>
              <DatePicker
                selected={parseYmdHm(startDate)}
                onChange={(date) => setStartDate(date ? formatYmdHm(date) : "")}
                placeholderText={translations[langMode].startdatetime}
                showTimeSelect
                timeIntervals={1}
                dateFormat="dd/MM/yyyy, HH:mm"
                maxDate={todayPicker}
                wrapperClassName="w-full"
                className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                calendarClassName="!rounded-lg shadow-lg"
                popperPlacement="bottom-start"
                popperContainer={datePickerPopper}
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium">{translations[langMode].enddatetime}</label>
              <DatePicker
                selected={parseYmdHm(endDate)}
                onChange={(date) => setEndDate(date ? formatYmdHm(date) : "")}
                placeholderText={translations[langMode].enddatetime}
                showTimeSelect
                timeIntervals={1}
                dateFormat="dd/MM/yyyy, HH:mm"
                minDate={parseYmdHm(startDate)}
                maxDate={todayPicker}
                disabled={!startDate}
                wrapperClassName="w-full"
                className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                calendarClassName="!rounded-lg shadow-lg"
                popperPlacement="bottom-start"
                popperContainer={datePickerPopper}
              />
            </div>
            </div>
            <div className="flex justify-end gap-3">
              <CommonButton colorIntent="danger"
              variant="danger"
              tooltipText={translations[langMode].cancelandclose}
              tooltipPosition="top"
                onClick={() => setShowDateModal(false)}
              >
                {translations[langMode].cancel}
              </CommonButton>
              <CommonButton colorIntent="primary"
               tooltipText={translations[langMode].applytheselecteddaterange}
                tooltipPosition="top"
                onClick={() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0); // Normalize today's date to midnight for comparison
                  const selectedStartDate = new Date(startDate);
                  const selectedEndDate = new Date(endDate);
                  if (selectedStartDate > new Date()) {
                    toast.error((translations[langMode].dateCannotBeFuture || "{field} cannot be in the future.").replace("{field}", translations[langMode].startdatetime));
                    return;
                  }
                  if (selectedEndDate > new Date()) {
                    toast.error((translations[langMode].dateCannotBeFuture || "{field} cannot be in the future.").replace("{field}", translations[langMode].enddatetime));
                    return;
                  }
                  if (selectedStartDate > selectedEndDate) {
                    toast.error(translations[langMode].startdateerror);
                    return;
                  }
                  setShowDateModal(false);
                  // fetchData(); // redundant: startDate/endDate are bound
                  // directly to the inputs above, and the mount effect
                  // (deps: [selectedWorkflowId, langMode, translations,
                  // startDate, endDate]) already re-fetches as soon as either
                  // date changes -- by the time Apply is clickable, that
                  // fetch already ran with these exact values.

                }}
              
                variant='primary'
              >
                {translations[langMode].apply}
              </CommonButton>
            </div>
          </div>
        </div>
      )}
      <ChartModal
        showModal={showModal}
        setShowModal={setShowModal}
        setChartView={setChartView}
        currentChartView={chartView}
        availableChartTypes={["radialBar", "bar", "area", "radar"]}
      />
      <Modal
        setDownloadModal={setDownloadModal}
        downloadModal={downloadModal}
        downloadFiltration={downloadFiltration}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setPriority={setPriority}
        priority={priority}
        type="task"
        status={status}
        setStatus={setStatus}
      />
    </div>
  );
};

export default TaskStatusChart;
